import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { tap, filter, takeUntil } from 'rxjs/operators';
import { pickBy, omitBy, mergeWith, isNil } from 'lodash';
import { Store } from '@ngrx/store';

import * as fromStore from '@app/core/store';
import { ScreenComponent } from '@core/features/screen.component';
import * as fromMaster from '@features/master/master-home/store';

import { FeatureService } from '@services/feature/feature.service';

import { CustomAuthenticationService } from '@app/auth/services/custom-authentication.service';
import { PermissionsType } from '../../../custom-shared/enums/permissions-type.enum';

/**
 * This Component manages the presentation logic of Edit screen
 *
 */
@Component({
  selector: 'app-master-home',
  templateUrl: './master-home.component.html',
  styleUrls: ['./master-home.component.scss'],
})
export class MasterHomeComponent extends ScreenComponent implements OnInit {
  vars: any = {};
  loading$: Observable<boolean>;
  data$: Observable<any>;
  showConsultaUsuario: boolean;
  userProfile: string;
  showMasterConsultaPerfil: boolean;
  showMasterBOConsultaPerfil: boolean;

  keys = ``;

  /**
   * Default Constructor of the component
   */
  constructor(
    private featureService: FeatureService,
    private customAuthenticationService: CustomAuthenticationService,
    router: Router,
    store: Store<fromMaster.State>,
    activeRoute: ActivatedRoute,
  ) {
    super(activeRoute, store, router);
  }

  ngOnInit() {
    super.ngOnInit();
    this.id = this.activeRoute.snapshot.data.inputs.id;
    this.form = this.createForm();
    this.parentCtx = this.getParentCtxParam();
    this.ctx = this.getScreenCtx();
    this.store.dispatch(fromMaster.initStateAction({ ...this.params }));
    this.store.dispatch(
      fromMaster.initValueAction({
        ...this.getPayload(),
        activeRoute: this.activeRoute,
      }),
    );

    this.data$ = this.store.pipe(
      fromStore.selectData(this.ctx),
      tap(item => this.init(item)),
    );
    this.loading$ = this.store.select(fromStore.selectLoading, this.ctx);
    this.store
      .select(fromStore.checkValidation, this.ctx)
      .pipe(filter(value => value))
      .subscribe(data => {
        const isValid = this.form.valid;
        if (!isValid) {
          this.featureService.validateForm(this.form);
        }
        this.store.dispatch(
          fromMaster.updateDataAction({
            ...this.getActionPayload(),
            isValid,
            value: { ...this.data },
          }),
        );
      });
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.data = mergeWith({ ...this.data }, { ...value }, (target, source) =>
        source === null || source instanceof Array ? source : undefined,
      );
    });
  }

  /**
   * Initialize the component by the provided data object
   * @param item
   */
  init(item) {
    this.data = { ...pickBy(this.data), ...omitBy(item, isNil) };
    this.featureService.patchValue(this.data, this.form);
  }

  getPayload() {
    return {
      ...this.params,
      ...this.ctx,
      code: this.getCode(),
      keys: this.keys,
      parentCtx: { ...this.parentCtx },
    };
  }

  private getCode() {
    return this.parentCtx && this.parentCtx.code
      ? this.parentCtx.code
      : this.getParamCode();
  }
  private getParamCode() {
    const id = this.activeRoute.snapshot.params.id;
    return id && !id.startsWith('{') ? id : undefined;
  }

  private getParentCtxParam() {
    const id = this.activeRoute.snapshot.params.id;
    return id && id.startsWith('{') ? JSON.parse(id) : this.parentCtx;
  }

  getScreenCtx() {
    return fromStore.buildScreenCtx(
      'Master',
      'MasterHome',
      this.screenId,
      this.parentCtx ? this.parentCtx.roleName : null,
      this.parentCtx ? this.parentCtx.rootId : null,
      this.parentCtx && this.parentCtx.roleName
        ? this.screenId + ',' + this.parentCtx.screenId
        : null,
    );
  }

  /**
   * Get the data to dispatch by ngrx actions
   */
  getActionPayload() {
    return {
      ...this.params,
      ctx: { ...this.ctx },
      activeRoute: this.activeRoute,
      vars: { ...this.vars },
      keys: this.keys,
      data: { ...this.data },
      code: this.getCode(),
    };
  }

  createForm(): FormGroup {
    return this.featureService.formBuilder.group({});
  }

  checkRouteProfile(route) {
    return route == this.router.url;
  }

  isMasterCliente() {
    return (
      this.customAuthenticationService.isMasterCliente() ||
      this.checkRouteProfile('/master/master-home')
    );
  }

  isMasterBO() {
    return (
      this.customAuthenticationService.isMasterBO() &&
      this.checkRouteProfile('/master/backoffice')
    );
  }

  hasAccessToClients() {
    return (
      this.customAuthenticationService.hasPermission(
        PermissionsType.cadastrarUsuarios,
      ) ||
      this.customAuthenticationService.hasPermission(
        PermissionsType.editarUsuarios,
      )
    );
  }

  hasAccessToProfiles() {
    return (
      this.customAuthenticationService.hasPermission(
        PermissionsType.editarPerfilDeAcesso,
      ) ||
      this.customAuthenticationService.hasPermission(
        PermissionsType.criarPerfilDeAcesso,
      )
    );
  }

  hasAccessToInvestmentGroup() {
    return this.customAuthenticationService.hasPermission(PermissionsType.consultarGruposDeInvestimentos);
  }

  hasAccessToConsultFounds() {
    return this.customAuthenticationService.hasPermission(
      PermissionsType.consultarFundos,
    );
  }

  /**
   * Navigate to an auxiliary router outlet
   * @param {object} props
   */
  navigate(
    feature: string,
    screen: string,
    navigationType: string,
    roleName: string,
  ): void {
    this.featureService.navigate(
      feature,
      screen,
      navigationType,
      JSON.stringify({
        ...this.ctx,
        roleName,
        rootId: this.ctx.screenId,
      }),
      this.activeRoute,
    );
  }
  /**
   * Get date format from the profile
   * @param {string} temporalType
   * @param {string} datePrecision
   * @return {string} the date format
   */
  getDateFormat(temporalType: string, datePrecision?: string): string {
    return this.featureService.profileService.getDateFormat(
      temporalType,
      datePrecision,
    );
  }
}
